import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, Check, X, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIETARY_OPTIONS = [
  { id: "glutine", label: "Intolleranza al glutine" },
  { id: "lattosio", label: "Intolleranza al lattosio" },
  { id: "frutta_secca", label: "Allergia alla frutta secca" },
  { id: "vegano", label: "Vegano" },
  { id: "vegetariano", label: "Vegetariano" },
];

export default function RSVPPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [attending, setAttending] = useState(null);
  const [participants, setParticipants] = useState(1);
  const [dietary, setDietary] = useState([]);
  const [otherDietary, setOtherDietary] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchGuests = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/guests/search`, { params: { q } });
      setResults(res.data);
      setShowResults(true);
    } catch (err) {
      console.error("Errore ricerca invitati:", err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchGuests(query), 300);
    return () => clearTimeout(timer);
  }, [query, searchGuests]);

  const selectGuest = (guest) => {
    setSelectedGuest(guest);
    setShowResults(false);
    setQuery("");
    setResults([]);
    if (guest.confermato !== null) {
      setSubmitted(true);
    }
    localStorage.setItem(
      "weddingGuest",
      JSON.stringify({ id: guest.id, nome: guest.nome, cognome: guest.cognome })
    );
  };

  const toggleDietary = (id) => {
    setDietary((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (attending === null) {
      toast.error("Indica se parteciperai o meno");
      return;
    }
    if (attending && participants < 1) {
      toast.error("Indica almeno 1 partecipante");
      return;
    }

    setSubmitting(true);
    try {
      const allDietary = [...dietary];
      if (otherDietary.trim()) allDietary.push(otherDietary.trim());

      await axios.post(`${API}/rsvp`, {
        guest_id: selectedGuest.id,
        confermato: attending,
        numero_partecipanti: attending ? participants : 0,
        intolleranze: attending ? allDietary : [],
        note,
      });

      setSubmitted(true);
      toast.success(
        attending
          ? "Grazie! La tua presenza è confermata."
          : "Grazie per averci risposto. Ci mancherai!"
      );
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore nell'invio del RSVP";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="rsvp-page"
      className="min-h-screen pt-20 pb-16 bg-warm-white"
    >
      <div className="max-w-xl mx-auto px-6">
        {/* Header */}
        <div className="text-center py-10">
          <h1
            className="font-serif text-4xl sm:text-5xl mb-2"
            style={{ color: "#2C2C2C" }}
          >
            RSVP
          </h1>
          <div className="gold-line mb-4" />
          <p className="text-base" style={{ color: "#666666" }}>
            Conferma la tua presenza al nostro matrimonio
          </p>
        </div>

        {/* Search */}
        {!selectedGuest && (
          <div className="relative" data-testid="guest-search-container">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: "#8B9A6B" }}
              />
              <Input
                data-testid="guest-search-input"
                type="text"
                placeholder="Cerca il tuo nome..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setShowResults(true)}
                className="pl-10 h-12 text-base rounded-sm"
                style={{
                  backgroundColor: "white",
                  border: "1px solid rgba(197, 165, 114, 0.4)",
                }}
              />
              {searching && (
                <Loader2
                  size={18}
                  className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-sage"
                />
              )}
            </div>

            {showResults && results.length > 0 && (
              <div className="search-results" data-testid="search-results">
                {results.map((g) => (
                  <button
                    key={g.id}
                    data-testid={`guest-result-${g.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors duration-200 border-b last:border-b-0"
                    onClick={() => selectGuest(g)}
                  >
                    <span className="font-medium" style={{ color: "#2C2C2C" }}>
                      {g.nome}{g.cognome ? ` ${g.cognome}` : ""}
                    </span>
                    {g.gruppo && (
                      <span
                        className="text-sm ml-2"
                        style={{ color: "#666666" }}
                      >
                        ({g.gruppo})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {showResults && query.length >= 2 && results.length === 0 && !searching && (
              <div
                className="search-results px-4 py-4 text-center"
                data-testid="no-results"
              >
                <AlertCircle
                  size={20}
                  className="mx-auto mb-2"
                  style={{ color: "#C5A572" }}
                />
                <p className="text-sm" style={{ color: "#666666" }}>
                  Nessun invitato trovato. Verifica il tuo nome.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Already submitted */}
        {selectedGuest && submitted && (
          <div
            data-testid="rsvp-already-submitted"
            className="text-center bg-white rounded-sm p-8"
            style={{ border: "1px solid rgba(197, 165, 114, 0.3)" }}
          >
            <div
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                backgroundColor: selectedGuest.confermato
                  ? "#8B9A6B"
                  : "#E5E5E5",
              }}
            >
              {selectedGuest.confermato ? (
                <Check size={28} color="white" />
              ) : (
                <X size={28} color="#666" />
              )}
            </div>
            <h2
              className="font-serif text-2xl mb-2"
              style={{ color: "#2C2C2C" }}
            >
              {selectedGuest.nome}{selectedGuest.cognome ? ` ${selectedGuest.cognome}` : ""}
            </h2>
            <p className="text-base mb-2" style={{ color: "#666666" }}>
              {selectedGuest.confermato
                ? `Parteciperai con ${selectedGuest.numero_partecipanti} ${selectedGuest.numero_partecipanti === 1 ? "persona" : "persone"}`
                : "Non parteciperai"}
            </p>
            {selectedGuest.intolleranze?.length > 0 && (
              <p className="text-sm" style={{ color: "#666666" }}>
                Intolleranze: {selectedGuest.intolleranze.join(", ")}
              </p>
            )}
            <p className="text-sm mt-4" style={{ color: "#8B9A6B" }}>
              La tua risposta è stata registrata. Contatta gli sposi per eventuali modifiche.
            </p>
            <Button
              data-testid="rsvp-search-another"
              variant="outline"
              className="mt-6 rounded-sm"
              onClick={() => {
                setSelectedGuest(null);
                setSubmitted(false);
                setAttending(null);
                setParticipants(1);
                setDietary([]);
                setOtherDietary("");
                setNote("");
              }}
            >
              Cerca un altro invitato
            </Button>
          </div>
        )}

        {/* RSVP Form */}
        {selectedGuest && !submitted && (
          <div
            data-testid="rsvp-form"
            className="bg-white rounded-sm p-6 sm:p-8"
            style={{ border: "1px solid rgba(197, 165, 114, 0.3)" }}
          >
            {/* Guest name */}
            <div className="text-center mb-6">
              <h2
                className="font-serif text-2xl"
                style={{ color: "#2C2C2C" }}
              >
                {selectedGuest.nome}{selectedGuest.cognome ? ` ${selectedGuest.cognome}` : ""}
              </h2>
              {selectedGuest.gruppo && (
                <p className="text-sm" style={{ color: "#666666" }}>
                  {selectedGuest.gruppo}
                </p>
              )}
            </div>

            <Separator className="mb-6" />

            {/* Attendance toggle */}
            <div className="mb-6">
              <Label className="text-base font-medium mb-3 block" style={{ color: "#2C2C2C" }}>
                Parteciperai al matrimonio?
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="rsvp-attending-yes"
                  className="touch-target rounded-sm p-4 text-center transition-all duration-300 border-2"
                  style={{
                    backgroundColor:
                      attending === true ? "#8B9A6B" : "transparent",
                    color: attending === true ? "white" : "#2C2C2C",
                    borderColor:
                      attending === true ? "#8B9A6B" : "rgba(197,165,114,0.4)",
                  }}
                  onClick={() => setAttending(true)}
                >
                  <Check
                    size={24}
                    className="mx-auto mb-1"
                    style={{
                      color: attending === true ? "white" : "#8B9A6B",
                    }}
                  />
                  <span className="font-medium text-base">Parteciperò</span>
                </button>
                <button
                  data-testid="rsvp-attending-no"
                  className="touch-target rounded-sm p-4 text-center transition-all duration-300 border-2"
                  style={{
                    backgroundColor:
                      attending === false ? "#666666" : "transparent",
                    color: attending === false ? "white" : "#2C2C2C",
                    borderColor:
                      attending === false
                        ? "#666666"
                        : "rgba(197,165,114,0.4)",
                  }}
                  onClick={() => setAttending(false)}
                >
                  <X
                    size={24}
                    className="mx-auto mb-1"
                    style={{
                      color: attending === false ? "white" : "#666666",
                    }}
                  />
                  <span className="font-medium text-base">Non parteciperò</span>
                </button>
              </div>
            </div>

            {/* Conditional fields when attending */}
            {attending === true && (
              <>
                <Separator className="mb-6" />

                {/* Participants - Solo or Accompanied */}
                <div className="mb-6">
                  <Label className="text-base font-medium mb-3 block" style={{ color: "#2C2C2C" }}>
                    Verrai solo/a o accompagnato/a?
                  </Label>
                  {selectedGuest.max_accompagnatori <= 1 ? (
                    <p className="text-sm py-2 px-3 rounded-sm" style={{ backgroundColor: "#F5E6D3", color: "#2C2C2C" }}>
                      1 partecipante
                    </p>
                  ) : (
                    <div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <button
                          data-testid="rsvp-solo"
                          className="touch-target rounded-sm p-3 text-center transition-all duration-300 border-2"
                          style={{
                            backgroundColor: participants === 1 ? "#8B9A6B" : "transparent",
                            color: participants === 1 ? "white" : "#2C2C2C",
                            borderColor: participants === 1 ? "#8B9A6B" : "rgba(197,165,114,0.4)",
                          }}
                          onClick={() => setParticipants(1)}
                        >
                          <span className="font-medium text-base">Solo/a</span>
                        </button>
                        <button
                          data-testid="rsvp-accompanied"
                          className="touch-target rounded-sm p-3 text-center transition-all duration-300 border-2"
                          style={{
                            backgroundColor: participants > 1 ? "#8B9A6B" : "transparent",
                            color: participants > 1 ? "white" : "#2C2C2C",
                            borderColor: participants > 1 ? "#8B9A6B" : "rgba(197,165,114,0.4)",
                          }}
                          onClick={() => setParticipants(2)}
                        >
                          <span className="font-medium text-base">Accompagnato/a</span>
                        </button>
                      </div>
                      {participants > 1 && selectedGuest.max_accompagnatori > 2 && (
                        <div>
                          <Label className="text-sm mb-2 block" style={{ color: "#666666" }}>
                            Quanti sarete in totale? (max {selectedGuest.max_accompagnatori})
                          </Label>
                          <div className="flex items-center gap-3">
                            <button
                              data-testid="rsvp-participants-minus"
                              className="touch-target w-12 h-12 rounded-sm border flex items-center justify-center text-xl font-medium transition-colors"
                              style={{ borderColor: "rgba(197,165,114,0.4)" }}
                              onClick={() => setParticipants((p) => Math.max(2, p - 1))}
                              disabled={participants <= 2}
                            >
                              -
                            </button>
                            <span
                              data-testid="rsvp-participants-count"
                              className="font-serif text-2xl w-12 text-center"
                              style={{ color: "#2C2C2C" }}
                            >
                              {participants}
                            </span>
                            <button
                              data-testid="rsvp-participants-plus"
                              className="touch-target w-12 h-12 rounded-sm border flex items-center justify-center text-xl font-medium transition-colors"
                              style={{ borderColor: "rgba(197,165,114,0.4)" }}
                              onClick={() => setParticipants((p) => Math.min(selectedGuest.max_accompagnatori, p + 1))}
                              disabled={participants >= selectedGuest.max_accompagnatori}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Separator className="mb-6" />

                {/* Dietary restrictions */}
                <div className="mb-6">
                  <Label className="text-base font-medium mb-3 block" style={{ color: "#2C2C2C" }}>
                    Intolleranze alimentari
                  </Label>
                  <div className="space-y-3">
                    {DIETARY_OPTIONS.map((opt) => (
                      <div
                        key={opt.id}
                        className="flex items-center gap-3"
                      >
                        <Checkbox
                          id={opt.id}
                          data-testid={`dietary-${opt.id}`}
                          checked={dietary.includes(opt.id)}
                          onCheckedChange={() => toggleDietary(opt.id)}
                          className="h-5 w-5"
                        />
                        <Label
                          htmlFor={opt.id}
                          className="text-base cursor-pointer"
                          style={{ color: "#2C2C2C" }}
                        >
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                    <div className="pt-2">
                      <Input
                        data-testid="dietary-other-input"
                        placeholder="Altro (specifica)..."
                        value={otherDietary}
                        onChange={(e) => setOtherDietary(e.target.value)}
                        className="h-11 text-base rounded-sm"
                        style={{
                          border: "1px solid rgba(197,165,114,0.4)",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <Separator className="mb-6" />

            {/* Notes */}
            <div className="mb-6">
              <Label className="text-base font-medium mb-3 block" style={{ color: "#2C2C2C" }}>
                Note per gli sposi
              </Label>
              <textarea
                data-testid="rsvp-notes"
                placeholder="Scrivi qui eventuali note..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full rounded-sm p-3 text-base resize-none focus:outline-none focus:ring-1"
                style={{
                  border: "1px solid rgba(197,165,114,0.4)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              />
            </div>

            {/* Submit */}
            <Button
              data-testid="rsvp-submit-button"
              className="w-full btn-press touch-target text-base rounded-sm"
              style={{
                backgroundColor: "#8B9A6B",
                color: "white",
                minHeight: "48px",
              }}
              onClick={handleSubmit}
              disabled={submitting || attending === null}
            >
              {submitting ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : null}
              {submitting ? "Invio in corso..." : "Invia RSVP"}
            </Button>

            <button
              data-testid="rsvp-change-guest"
              className="w-full text-center mt-4 text-sm underline"
              style={{ color: "#666666" }}
              onClick={() => {
                setSelectedGuest(null);
                setAttending(null);
                setParticipants(1);
                setDietary([]);
                setOtherDietary("");
                setNote("");
              }}
            >
              Cerca un altro invitato
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
